import { useLocalSearchParams, useRouter } from 'expo-router';
import type { ReactElement } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import type { PeopleFilter } from '@/components/People/member-type';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import CheckboxRow from '@/components/UI/CheckboxRow';
import Text from '@/components/UI/Text';
import { openMemberFilter } from '@/stores/useMemberFilterStore';
import { toggleSelection } from '@/utils/selection';

export type OrganizationMemberFilterMenuOption = {
  avatarId?: string | null;
  color?: string | null;
  id: string;
  label: string;
};

type OrganizationMemberFilterMenuProps = {
  activeFilterCount: number;
  badgeIds: string[];
  includeMemberTypes?: boolean;
  includeTeams?: boolean;
  memberType?: PeopleFilter;
  organizationId?: string;
  targetKey: string;
  teamId?: string;
  teamIds?: string[];
  testIDPrefix: string;
  onOpenFilter?: () => void;
};

export default function OrganizationMemberFilterMenu({
  activeFilterCount,
  badgeIds,
  includeMemberTypes = false,
  includeTeams = false,
  memberType = 'all',
  organizationId,
  targetKey,
  teamId,
  teamIds = [],
  testIDPrefix,
  onOpenFilter,
}: OrganizationMemberFilterMenuProps) {
  const { push } = useRouter();
  const { organizationId: routeOrganizationId } = useLocalSearchParams<{
    organizationId?: string;
  }>();
  const filterOrganizationId = organizationId || routeOrganizationId || null;
  const filterButtonVariant = activeFilterCount ? 'info' : 'secondary';
  const filterButtonLabel = activeFilterCount
    ? `Filter (${activeFilterCount})`
    : 'Filter';

  return (
    <ActionButton
      forceLabel
      icon="filter-outline"
      label={filterButtonLabel}
      testID={`${testIDPrefix}FilterButton`}
      variant={filterButtonVariant}
      onPress={() => {
        onOpenFilter?.();
        openMemberFilter({
          badgeIds,
          includeMemberTypes,
          includeTeams,
          memberType,
          organizationId: filterOrganizationId,
          targetKey,
          teamId: teamId ?? null,
          teamIds,
          testIDPrefix,
        });
        push({
          pathname: '/member-action/filter',
        });
      }}
    />
  );
}

export function OrganizationMemberFilterSection({
  allLabel,
  emptyLabel,
  label,
  onChangeIds,
  options,
  renderPreview,
  selectedIds,
  testID,
}: {
  allLabel: string;
  emptyLabel: string;
  label: string;
  onChangeIds: (ids: string[]) => void;
  options: OrganizationMemberFilterMenuOption[];
  renderPreview: (option: OrganizationMemberFilterMenuOption) => ReactElement;
  selectedIds: string[];
  testID: string;
}) {
  const selectedCount = selectedIds.length;
  const sectionStatus = selectedCount ? `${selectedCount} selected` : 'All';

  return (
    <Box direction="vertical" py="xs">
      <Box
        direction="horizontal"
        align="center"
        justify="space-between"
        px="md"
        py="sm"
      >
        <Text style={styles.label}>{label}</Text>
        <Text size="xs" variant="typographySecondary">
          {sectionStatus}
        </Text>
      </Box>
      <CheckboxRow
        rounded
        style={selectedIds.length === 0 ? styles.selectedRow : undefined}
        value={selectedIds.length === 0}
        testID={`${testID}All`}
        onValueChange={() => {
          onChangeIds([]);
        }}
      >
        <Text numberOfLines={1}>{allLabel}</Text>
      </CheckboxRow>
      {options.length ? (
        options.map((option) => {
          const selected = selectedIds.includes(option.id);

          return (
            <CheckboxRow
              key={option.id}
              rounded
              style={selected ? styles.selectedRow : undefined}
              value={selected}
              testID={`${testID}-${option.id}`}
              onValueChange={(nextSelected) => {
                onChangeIds(
                  toggleSelection({
                    ids: selectedIds,
                    selected: nextSelected,
                    value: option.id,
                  }),
                );
              }}
            >
              <Box
                direction="horizontal"
                align="center"
                space="sm"
                minWidth={0}
              >
                {renderPreview(option)}
                <Text numberOfLines={1} flexShrink={1}>
                  {option.label}
                </Text>
              </Box>
            </CheckboxRow>
          );
        })
      ) : (
        <Text px="md" py="sm" variant="typographySecondary">
          {emptyLabel}
        </Text>
      )}
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  label: {
    color: theme.colors.typographySecondary,
    fontSize: theme.fontSize.md,
  },
  selectedRow: {
    backgroundColor: theme.colors.surfaceSecondary,
  },
}));
